import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock, ContentBlockId, SupplementBody } from "../../types";

const ALPHASPERM_PRODUCT_URL = "https://alphasperm.com/products/alpha-sperm";
const ALPHASPERM_LABEL_URL =
  "https://alphaspermdemo.myshopify.com/cdn/shop/files/AS-NutritionFacts-1130x1980.png?v=1777296224&width=3840";

type IngredientInput = {
  id: string;
  title: string;
  name: string;
  dosageRange: string;
  dailyTiming: string;
  timing?: string;
  evidenceStrength?: SupplementBody["evidenceStrength"];
  foods?: string[];
  form?: string;
  absorption?: string;
  metabolism?: string;
  canExpect: string[];
  shouldNotExpect: string[];
  sourceTitle: string;
  sourceUrl: string;
  sourceJournal?: string;
  sourceYear?: number;
};

function ingredientFactSheet(
  input: IngredientInput,
): ContentBlock<"supplement"> {
  return {
    id: makeId(input.id),
    kind: "supplement",
    tags: ["supplements", "his", "optimization", "pre-cycle", "sperm"],
    title: input.title,
    icon: "💊",
    body: {
      name: input.name,
      dosageRange: input.dosageRange,
      dailyTiming: input.dailyTiming,
      timing: input.timing ?? "Useful as part of a daily preconception stack",
      bioavailability: {
        form: input.form,
        absorption: input.absorption,
        metabolism: input.metabolism,
      },
      evidenceStrength: input.evidenceStrength ?? "emerging",
      forWhom: "him",
      foods: input.foods,
      expectations: {
        canExpect: input.canExpect,
        shouldNotExpect: input.shouldNotExpect,
      },
    },
    sources: [
      {
        title: input.sourceTitle,
        journal: input.sourceJournal,
        year: input.sourceYear,
        url: input.sourceUrl,
        evidenceLevel: "review",
      },
    ],
  };
}

function ingredient(
  name: string,
  amount: string,
  purpose: string,
  linkedBlockId: ContentBlockId,
  options: {
    category?: string;
    percentDailyValue?: string;
    form?: string;
  } = {},
): NonNullable<SupplementBody["ingredients"]>[number] {
  return {
    name,
    amount,
    purpose,
    linkedBlockId,
    ...options,
  };
}

const vitaminA = ingredientFactSheet({
  id: "supplement-vitamin-a",
  title: "Vitamin A",
  name: "Vitamin A",
  dosageRange:
    "Usually covered by a multivitamin; AlphaSperm provides 1,500mcg RAE per daily serving",
  dailyTiming:
    "Take with food, ideally with some fat. Avoid stacking high-dose vitamin A products.",
  evidenceStrength: "moderate",
  foods: ["Eggs", "Milk", "Spinach"],
  form: "AlphaSperm uses beta carotene plus retinyl palmitate, which combines a provitamin A carotenoid with preformed vitamin A.",
  absorption:
    "Vitamin A is fat-soluble, so absorption is better with a meal that contains fat.",
  metabolism:
    "Vitamin A supports cell differentiation and antioxidant defenses, but excess preformed vitamin A can be harmful.",
  canExpect: [
    "Supports antioxidant defense and normal cell differentiation during sperm development",
    "Contributes to immune and reproductive tissue health",
    "Works as part of a multinutrient formula rather than as a standalone fertility treatment",
  ],
  shouldNotExpect: [
    "More is not better; avoid stacking high-dose vitamin A or retinol products",
    "Do not treat it as a cure for abnormal semen analysis results",
    "Use extra caution with isotretinoin or liver disease history",
  ],
  sourceTitle: "Vitamin A Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl: "https://ods.od.nih.gov/factsheets/VitaminA-HealthProfessional/",
});

const vitaminK = ingredientFactSheet({
  id: "supplement-vitamin-k",
  title: "Vitamin K",
  name: "Vitamin K",
  dosageRange:
    "Usually met through diet or a multivitamin; AlphaSperm provides 100mcg per daily serving",
  dailyTiming:
    "Take consistently with food. If you take warfarin, do not change vitamin K intake without clinician guidance.",
  evidenceStrength: "emerging",
  foods: ["Leafy greens", "Broccoli", "Spinach"],
  form: "AlphaSperm uses phylloquinone, also called vitamin K1.",
  absorption:
    "Vitamin K is fat-soluble and absorbs better with meals containing fat.",
  metabolism:
    "It is best known for clotting-factor biology; animal data also connect vitamin K status with testicular function.",
  canExpect: [
    "Supports normal vitamin K intake inside the broader male prenatal formula",
    "May help cover a diet gap for people with low leafy-green intake",
    "Adds fat-soluble nutrient support without being a primary sperm supplement",
  ],
  shouldNotExpect: [
    "Direct proven improvement in sperm count, motility, or pregnancy outcomes",
    "Safe unsupervised use if you take warfarin or other anticoagulation",
    "A reason to add separate high-dose vitamin K on top of AlphaSperm",
  ],
  sourceTitle: "Vitamin K Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl: "https://ods.od.nih.gov/factsheets/VitaminK-HealthProfessional/",
});

const thiamin = ingredientFactSheet({
  id: "supplement-thiamin-b1",
  title: "Thiamin (B1)",
  name: "Thiamin / Vitamin B1",
  dosageRange: "AlphaSperm provides 4mg per daily serving",
  dailyTiming:
    "Take with the rest of the formula. Morning or afternoon dosing is easiest for most B vitamins.",
  evidenceStrength: "emerging",
  foods: ["Fortified grains", "Beans", "Pork"],
  form: "AlphaSperm uses thiamin hydrochloride.",
  absorption:
    "Thiamin is water-soluble, so consistent daily intake matters more than occasional large doses.",
  metabolism:
    "B1 helps convert food into cellular energy, which is relevant to high-energy tissues and sperm metabolism.",
  canExpect: [
    "Supports general energy metabolism in a male prenatal formula",
    "Helps cover B-vitamin gaps that are common with restricted diets",
    "Pairs with the rest of the B-complex nutrients used in AlphaSperm",
  ],
  shouldNotExpect: [
    "A standalone fertility effect from thiamin alone",
    "Immediate changes in semen parameters",
    "A replacement for a nutrition review if diet quality is poor",
  ],
  sourceTitle: "Thiamin Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl: "https://ods.od.nih.gov/factsheets/Thiamin-HealthProfessional/",
});

const riboflavin = ingredientFactSheet({
  id: "supplement-riboflavin-b2",
  title: "Riboflavin (B2)",
  name: "Riboflavin / Vitamin B2",
  dosageRange: "AlphaSperm provides 5mg per daily serving",
  dailyTiming:
    "Take with the rest of the formula. Bright yellow urine can happen and is usually harmless.",
  evidenceStrength: "emerging",
  foods: ["Eggs", "Milk", "Almonds"],
  form: "AlphaSperm uses riboflavin-5-phosphate.",
  absorption:
    "Riboflavin is water-soluble and works best as part of steady daily intake.",
  metabolism:
    "B2 is needed for flavin coenzymes used in mitochondrial energy production and antioxidant recycling.",
  canExpect: [
    "Supports mitochondrial and antioxidant enzyme systems",
    "Complements other B vitamins in sperm-energy pathways",
    "Helps fill a basic micronutrient gap rather than acting like a drug",
  ],
  shouldNotExpect: [
    "Direct proven improvement in fertility outcomes from riboflavin alone",
    "Meaningful benefit if overall B-vitamin intake is already adequate",
    "A substitute for testing or treatment of male factor infertility",
  ],
  sourceTitle: "Riboflavin Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl: "https://ods.od.nih.gov/factsheets/Riboflavin-HealthProfessional/",
});

const niacin = ingredientFactSheet({
  id: "supplement-niacin-b3",
  title: "Niacin (B3)",
  name: "Niacin / Vitamin B3",
  dosageRange: "AlphaSperm provides 30mg NE per daily serving",
  dailyTiming:
    "Take with food if it causes flushing or stomach upset. AlphaSperm uses niacinamide, which is less flushing than nicotinic acid.",
  evidenceStrength: "emerging",
  foods: ["Chicken", "Tuna", "Fortified grains"],
  form: "AlphaSperm uses niacinamide.",
  absorption:
    "Niacin forms are water-soluble; daily consistency is the main practical point.",
  metabolism:
    "B3 supports NAD/NADP pathways used in energy metabolism and cellular repair.",
  canExpect: [
    "Supports general cellular energy and repair chemistry",
    "Adds B-complex coverage in a formula built around sperm energy and oxidative balance",
    "May be easier to tolerate in niacinamide form",
  ],
  shouldNotExpect: [
    "A direct semen-analysis change from niacin alone",
    "A reason to add separate high-dose niacin, which can carry liver and glucose risks",
    "Flush-free always meaning risk-free at high doses",
  ],
  sourceTitle: "Niacin Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl: "https://ods.od.nih.gov/factsheets/Niacin-HealthProfessional/",
});

const vitaminB6 = ingredientFactSheet({
  id: "supplement-vitamin-b6",
  title: "Vitamin B6",
  name: "Vitamin B6",
  dosageRange: "AlphaSperm provides 10mg per daily serving",
  dailyTiming:
    "Take with the rest of the formula. Avoid adding separate high-dose B6 unless directed.",
  evidenceStrength: "moderate",
  foods: ["Chicken", "Tuna", "Chickpeas"],
  form: "AlphaSperm uses pyridoxyl-5-phosphate, the active P5P form.",
  absorption:
    "B6 is water-soluble; excessive long-term dosing is the bigger issue than absorption.",
  metabolism:
    "B6 participates in amino-acid metabolism, neurotransmitter synthesis, and homocysteine handling.",
  canExpect: [
    "Supports amino-acid and methylation-adjacent pathways in a male prenatal formula",
    "Works with folate and B12 in one-carbon metabolism",
    "Helps avoid a common B-vitamin gap without megadosing",
  ],
  shouldNotExpect: [
    "Better fertility from pushing B6 above the safe range",
    "A standalone treatment for sperm count or morphology problems",
    "Safe indefinite high-dose use; chronic excess can cause nerve symptoms",
  ],
  sourceTitle: "Vitamin B6 Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl: "https://ods.od.nih.gov/factsheets/VitaminB6-HealthProfessional/",
});

const vitaminB12 = ingredientFactSheet({
  id: "supplement-vitamin-b12",
  title: "Vitamin B12",
  name: "Vitamin B12",
  dosageRange: "AlphaSperm provides 1,000mcg per daily serving",
  dailyTiming:
    "Take with the rest of the formula. People using metformin, PPIs, or vegan diets may need extra B12 attention.",
  evidenceStrength: "moderate",
  foods: ["Eggs", "Milk", "Fish"],
  form: "AlphaSperm uses methylcobalamin.",
  absorption:
    "B12 absorption depends on intrinsic factor and gut health; high oral doses can still absorb partly by passive diffusion.",
  metabolism:
    "B12 works with folate in DNA synthesis and methylation, both relevant to rapidly dividing cells.",
  canExpect: [
    "Supports DNA synthesis and methylation biology in sperm production",
    "Helps cover low intake in vegan or low-animal-food diets",
    "Pairs with methylfolate in the formula",
  ],
  shouldNotExpect: [
    "A guaranteed fertility benefit if B12 status is already normal",
    "Correction of pernicious anemia or malabsorption without medical care",
    "A replacement for checking B12 if symptoms or risk factors are present",
  ],
  sourceTitle: "Vitamin B12 Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl: "https://ods.od.nih.gov/factsheets/VitaminB12-HealthProfessional/",
});

const biotin = ingredientFactSheet({
  id: "supplement-biotin-b7",
  title: "Biotin (B7)",
  name: "Biotin / Vitamin B7",
  dosageRange: "AlphaSperm provides 300mcg per daily serving",
  dailyTiming:
    "Take with the rest of the formula. Tell your care team about biotin before thyroid, hormone, or cardiac blood tests.",
  evidenceStrength: "emerging",
  foods: ["Eggs", "Salmon", "Sunflower seeds"],
  form: "AlphaSperm lists biotin as the active nutrient.",
  absorption:
    "Biotin is water-soluble and is usually absorbed well at nutritional doses.",
  metabolism:
    "Biotin is a cofactor for enzymes involved in fatty-acid and glucose metabolism.",
  canExpect: [
    "Supports basic energy and fatty-acid metabolism",
    "Rounds out the B-vitamin coverage in the formula",
    "May help correct a low-intake gap, though true deficiency is uncommon",
  ],
  shouldNotExpect: [
    "Direct sperm or pregnancy-outcome improvement from biotin alone",
    "No lab-test impact; biotin can interfere with some immunoassays",
    "A reason to add separate hair/nail biotin on top of the formula",
  ],
  sourceTitle: "Biotin Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl: "https://ods.od.nih.gov/factsheets/Biotin-HealthProfessional/",
});

const pantothenicAcid = ingredientFactSheet({
  id: "supplement-pantothenic-acid-b5",
  title: "Pantothenic Acid (B5)",
  name: "Pantothenic Acid / Vitamin B5",
  dosageRange: "AlphaSperm provides 10mg per daily serving",
  dailyTiming:
    "Take with the rest of the formula. Food can improve tolerance if B vitamins cause nausea.",
  evidenceStrength: "emerging",
  foods: ["Chicken", "Eggs", "Broccoli"],
  form: "AlphaSperm uses d-calcium pantothenate.",
  absorption:
    "B5 is water-soluble, so steady daily intake is preferred over occasional large dosing.",
  metabolism:
    "B5 is part of coenzyme A biology, which is central to energy metabolism.",
  canExpect: [
    "Supports cellular energy metabolism through coenzyme A pathways",
    "Complements L-carnitine and other sperm-energy nutrients",
    "Helps make the formula a complete B-vitamin stack",
  ],
  shouldNotExpect: [
    "A standalone male-fertility effect from B5 alone",
    "Rapid semen changes",
    "A need for separate high-dose B5 unless specifically advised",
  ],
  sourceTitle: "Pantothenic Acid Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl:
    "https://ods.od.nih.gov/factsheets/PantothenicAcid-HealthProfessional/",
});

const copper = ingredientFactSheet({
  id: "supplement-copper",
  title: "Copper",
  name: "Copper",
  dosageRange: "AlphaSperm provides 2mg per daily serving",
  dailyTiming:
    "Take with the formula. Avoid adding separate copper unless a clinician recommends it.",
  evidenceStrength: "emerging",
  foods: ["Oysters", "Organ meats", "Nuts"],
  form: "AlphaSperm uses copper bisglycinate chelate.",
  absorption:
    "Chelated minerals are designed for tolerability and absorption, but dose balance still matters.",
  metabolism:
    "Copper supports antioxidant enzymes and balances zinc intake; too little or too much can be a problem.",
  canExpect: [
    "Balances the formula's zinc content so zinc does not push copper too low",
    "Supports copper-dependent antioxidant enzymes",
    "Helps maintain a mineral stack rather than targeting sperm directly",
  ],
  shouldNotExpect: [
    "A reason to take extra copper without lab or clinician guidance",
    "Direct improvement in semen parameters from copper alone",
    "Safe high-dose use in people with copper metabolism disorders",
  ],
  sourceTitle: "Copper Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl: "https://ods.od.nih.gov/factsheets/Copper-HealthProfessional/",
});

const manganese = ingredientFactSheet({
  id: "supplement-manganese",
  title: "Manganese",
  name: "Manganese",
  dosageRange: "AlphaSperm provides 2mg per daily serving",
  dailyTiming:
    "Take with the formula. Avoid stacking multiple manganese products.",
  evidenceStrength: "emerging",
  foods: ["Brown rice", "Nuts", "Leafy greens"],
  form: "AlphaSperm uses manganese bisglycinate chelate.",
  absorption:
    "Manganese absorption is regulated by the body and can be affected by iron status.",
  metabolism:
    "Manganese is a cofactor for mitochondrial manganese superoxide dismutase, an antioxidant enzyme.",
  canExpect: [
    "Supports antioxidant-enzyme function in mitochondria",
    "Contributes to the broad antioxidant/mineral design of AlphaSperm",
    "Helps fill trace-mineral gaps without being a primary fertility treatment",
  ],
  shouldNotExpect: [
    "A direct proven semen-analysis benefit from manganese alone",
    "Benefit from high-dose stacking; excess manganese can be harmful",
    "A replacement for addressing occupational or environmental exposures",
  ],
  sourceTitle: "Manganese Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl: "https://ods.od.nih.gov/factsheets/Manganese-HealthProfessional/",
});

const chromium = ingredientFactSheet({
  id: "supplement-chromium",
  title: "Chromium",
  name: "Chromium",
  dosageRange: "AlphaSperm provides 100mcg per daily serving",
  dailyTiming:
    "Take with the formula. Discuss first if you use diabetes medication.",
  evidenceStrength: "emerging",
  foods: ["Broccoli", "Fortified grains", "Meat"],
  form: "AlphaSperm uses chromium picolinate.",
  absorption:
    "Chromium picolinate is a common supplemental form, but clinical effects vary.",
  metabolism:
    "Chromium is mostly discussed in glucose and insulin signaling, which can indirectly matter for reproductive health.",
  canExpect: [
    "Supports metabolic-health framing inside the male prenatal",
    "May be relevant when insulin resistance is part of the bigger picture",
    "Adds trace-mineral coverage at a nutritional dose",
  ],
  shouldNotExpect: [
    "A sperm-specific effect from chromium alone",
    "Safe unsupervised stacking with glucose-lowering medication or supplements",
    "A substitute for metabolic testing or lifestyle treatment",
  ],
  sourceTitle: "Chromium Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl: "https://ods.od.nih.gov/factsheets/Chromium-HealthProfessional/",
});

const molybdenum = ingredientFactSheet({
  id: "supplement-molybdenum",
  title: "Molybdenum",
  name: "Molybdenum",
  dosageRange: "AlphaSperm provides 75mcg per daily serving",
  dailyTiming:
    "Take with the formula. Separate supplementation is rarely needed unless directed.",
  evidenceStrength: "emerging",
  foods: ["Legumes", "Grains", "Nuts"],
  form: "AlphaSperm uses molybdenum glycinate chelate.",
  absorption:
    "Molybdenum is generally absorbed well from foods and supplements.",
  metabolism:
    "It is a cofactor for enzymes involved in sulfur-amino-acid and sulfite metabolism.",
  canExpect: [
    "Supports trace-mineral completeness in the formula",
    "Contributes to sulfur-metabolism enzyme systems",
    "Helps avoid gaps without being a headline fertility ingredient",
  ],
  shouldNotExpect: [
    "A direct fertility boost from molybdenum alone",
    "Need for separate molybdenum for most people",
    "Benefit from high-dose trace-mineral stacking",
  ],
  sourceTitle: "Molybdenum Fact Sheet for Health Professionals",
  sourceJournal: "NIH Office of Dietary Supplements",
  sourceYear: 2025,
  sourceUrl: "https://ods.od.nih.gov/factsheets/Molybdenum-HealthProfessional/",
});

const lCysteine = ingredientFactSheet({
  id: "supplement-l-cysteine",
  title: "L-Cysteine",
  name: "L-Cysteine",
  dosageRange:
    "AlphaSperm provides 300mg L-cysteine hydrochloride per daily serving",
  dailyTiming:
    "Take with the formula. Avoid doubling with high-dose NAC unless your clinician agrees.",
  evidenceStrength: "emerging",
  foods: ["Chicken", "Eggs", "Legumes"],
  form: "AlphaSperm uses L-cysteine hydrochloride.",
  absorption:
    "Cysteine is an amino acid; supplemental forms contribute to the body's cysteine pool.",
  metabolism:
    "Cysteine is a building block for glutathione, one of the body's major antioxidants.",
  canExpect: [
    "Supports glutathione production and antioxidant capacity",
    "Pairs logically with reduced glutathione and alpha-lipoic acid in the formula",
    "May help oxidative-stress pathways that are relevant to sperm DNA integrity",
  ],
  shouldNotExpect: [
    "The same evidence base as NAC, even though they are related",
    "Guaranteed improvement in DNA fragmentation or semen parameters",
    "Safe stacking with multiple cysteine/NAC/glutathione products without checking totals",
  ],
  sourceTitle: "N-acetyl-cysteine in male reproductive health",
  sourceJournal: "Antioxidants",
  sourceYear: 2021,
  sourceUrl: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8228088/",
});

const glutathione = ingredientFactSheet({
  id: "supplement-glutathione",
  title: "Glutathione",
  name: "L-Glutathione",
  dosageRange:
    "AlphaSperm provides 100mg reduced L-glutathione per daily serving",
  dailyTiming:
    "Take with the formula. Glutathione products vary widely in form and absorption claims.",
  evidenceStrength: "emerging",
  foods: ["Broccoli", "Spinach", "Avocado"],
  form: "AlphaSperm uses reduced L-glutathione.",
  absorption:
    "Oral glutathione bioavailability is debated; liposomal and reduced forms are marketed to improve exposure.",
  metabolism:
    "Glutathione helps neutralize reactive oxygen species and recycle other antioxidants.",
  canExpect: [
    "Adds direct antioxidant support to a formula focused on oxidative stress",
    "Works alongside vitamin C, vitamin E, selenium, alpha-lipoic acid, and cysteine",
    "May be relevant when sperm oxidative stress or DNA fragmentation is a concern",
  ],
  shouldNotExpect: [
    "Clear standalone fertility outcome data from glutathione alone",
    "Identical effects across all glutathione forms",
    "A replacement for lifestyle changes that reduce oxidative stress",
  ],
  sourceTitle: "Glutathione and reproductive health",
  sourceJournal: "Antioxidants",
  sourceYear: 2022,
  sourceUrl: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8952544/",
});

const lMethionine = ingredientFactSheet({
  id: "supplement-l-methionine",
  title: "L-Methionine",
  name: "L-Methionine",
  dosageRange: "AlphaSperm provides 25mg per daily serving",
  dailyTiming:
    "Take with the formula. Avoid adding separate high-dose methionine unless directed.",
  evidenceStrength: "emerging",
  foods: ["Eggs", "Fish", "Chicken"],
  form: "AlphaSperm uses L-methionine.",
  absorption:
    "Methionine is an essential amino acid absorbed from food and supplements.",
  metabolism:
    "Methionine feeds one-carbon and methylation pathways connected to DNA methylation biology.",
  canExpect: [
    "Supports methylation chemistry alongside folate, B12, B6, and choline",
    "Fits the formula's sperm epigenetic-health framing",
    "Contributes a small amount rather than a pharmacologic dose",
  ],
  shouldNotExpect: [
    "A proven standalone sperm epigenetics effect",
    "A reason to add high-dose methionine, which can raise homocysteine concerns",
    "Benefit without adequate folate, B12, and B6 context",
  ],
  sourceTitle: "Methionine metabolism and methylation biology",
  sourceJournal: "Nutrients",
  sourceYear: 2020,
  sourceUrl: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7468923/",
});

const lycopene = ingredientFactSheet({
  id: "supplement-lycopene",
  title: "Lycopene",
  name: "Lycopene",
  dosageRange: "AlphaSperm provides 20mg lycopene extract 5% per daily serving",
  dailyTiming:
    "Take with food that contains fat. Tomato-based foods with oil can also provide lycopene.",
  evidenceStrength: "emerging",
  foods: ["Tomatoes", "Watermelon", "Pink grapefruit"],
  form: "AlphaSperm uses lycopene extract from tomato.",
  absorption:
    "Lycopene is fat-soluble and is absorbed better with fat-containing meals.",
  metabolism:
    "It is a carotenoid antioxidant found in reproductive tissues and semen.",
  canExpect: [
    "Supports antioxidant coverage aimed at sperm oxidative stress",
    "May be relevant to motility, morphology, and DNA-protection pathways",
    "Complements vitamins A/C/E, selenium, CoQ10, and glutathione",
  ],
  shouldNotExpect: [
    "Guaranteed semen improvement from lycopene alone",
    "The same effect from all tomato or lycopene products",
    "A replacement for medical evaluation of varicocele, infection, or endocrine causes",
  ],
  sourceTitle: "Lycopene and male infertility",
  sourceJournal: "Asian Journal of Andrology",
  sourceYear: 2019,
  sourceUrl: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6734605/",
});

const astragalus = ingredientFactSheet({
  id: "supplement-astragalus",
  title: "Astragalus",
  name: "Astragalus extract",
  dosageRange:
    "AlphaSperm provides 0.30mg astragalus extract per daily serving",
  dailyTiming:
    "Take with the formula. Avoid adding separate immune-active herbs without clinician input.",
  evidenceStrength: "emerging",
  foods: [],
  form: "AlphaSperm uses Astragalus membranaceus root extract.",
  absorption:
    "Herbal extracts vary by concentration, plant part, and marker compounds.",
  metabolism:
    "Astragalus is usually discussed as an adaptogenic or immune-modulating botanical, not a core semen-analysis nutrient.",
  canExpect: [
    "Adds a small botanical component to a broader antioxidant formula",
    "May support general stress and immune-balance framing",
    "Is best viewed as supportive context, not the main fertility mechanism",
  ],
  shouldNotExpect: [
    "Direct clinical fertility outcomes from astragalus at this dose",
    "Safe use with immune suppression, autoimmune disease, or transplant medication without clinician review",
    "Standardization across all astragalus products",
  ],
  sourceTitle: "Astragalus",
  sourceJournal: "NCCIH",
  sourceYear: 2025,
  sourceUrl: "https://www.nccih.nih.gov/health/astragalus",
});

const alphaSperm: ContentBlock<"supplement"> = {
  id: makeId("supplement-alphasperm"),
  kind: "supplement",
  tags: [
    "supplements",
    "his",
    "optimization",
    "pre-cycle",
    "sperm",
    "evidence-based",
  ],
  title: "AlphaSperm",
  icon: "💊",
  lastReviewed: "2026-06-24",
  body: {
    name: "AlphaSperm Men's Prenatal+",
    aliases: [
      "AlphaSperm",
      "Alpha Sperm",
      "AlphaSperm Prenatal+",
      "AlphaSperm Men's Prenatal",
      "Men's Prenatal+",
    ],
    dosageRange:
      "4 capsules daily: 2 capsules in the morning and 2 capsules in the afternoon",
    dailyTiming:
      "Take two capsules twice daily, once in the morning and once in the afternoon. Each bottle is a 30-day supply.",
    timing:
      "Start at least 3 months before trying to conceive, IVF/IUI, or a planned sperm sample when possible, because a sperm-development cycle takes about 74 days.",
    bioavailability: {
      form: "Capsule formula with methylfolate, methylcobalamin, P5P vitamin B6, chelated trace minerals, amino acids, antioxidants, and botanical extracts.",
      absorption:
        "The split morning/afternoon schedule helps keep the 4-capsule daily serving tolerable. Fat-soluble nutrients such as vitamins A, D, E, K, CoQ10, lycopene, and resveratrol absorb better with food containing fat.",
      metabolism:
        "The formula combines antioxidant defense, mitochondrial energy support, methylation nutrients, trace minerals, and adaptogenic botanicals aimed at the 2-3 month sperm-development window.",
      cautions: [
        "Check the full stack before combining with another multivitamin, male fertility supplement, zinc, selenium, iodine, vitamin A, vitamin K, ashwagandha, NAC, glutathione, or CoQ10 product.",
        "Ask a clinician first if you take warfarin or anticoagulants, thyroid medication, diabetes medication, immune-suppressing medication, psychiatric medication, or have thyroid, liver, kidney, autoimmune, or cancer history.",
      ],
    },
    evidenceStrength: "emerging",
    forWhom: "him",
    expectations: {
      canExpect: [
        "A broad male prenatal-style stack focused on sperm oxidative stress, mitochondrial energy, methylation, and trace mineral support",
        "A more complete formula than taking a single antioxidant alone, with more than 30 active nutrients on the official label",
        "The most realistic reassessment window is about 3 months, because newly produced sperm need time to appear in the ejaculate",
        "The formula is positioned for male reproductive health and may be useful when oxidative stress, motility, DNA fragmentation, or preconception nutrition are part of the plan",
      ],
      shouldNotExpect: [
        "A guaranteed pregnancy, live birth, or normalized semen analysis result",
        "An instant effect; sperm development takes roughly 74 days, and clinics often retest after 2-3 months",
        "A replacement for semen analysis, reproductive urology evaluation, varicocele assessment, infection/endocrine workup, or lifestyle changes",
        "A simple add-on if you already take a multivitamin or fertility stack; duplicate zinc, selenium, vitamin A, iodine, B6, and fat-soluble vitamins can matter",
      ],
    },
    ingredients: [
      ingredient(
        "Vitamin A",
        "1,500mcg RAE",
        "Antioxidant and reproductive-tissue support.",
        makeId("supplement-vitamin-a"),
        {
          category: "Vitamins",
          percentDailyValue: "167%",
          form: "Beta carotene, retinyl palmitate",
        },
      ),
      ingredient(
        "Vitamin C",
        "300mg",
        "Water-soluble antioxidant that helps recycle other antioxidants.",
        makeId("supplement-vitamin-c"),
        {
          category: "Vitamins",
          percentDailyValue: "333%",
          form: "Calcium ascorbate",
        },
      ),
      ingredient(
        "Vitamin D",
        "25mcg",
        "Hormone-like vitamin linked with reproductive and immune health.",
        makeId("supplement-vitamin-d"),
        {
          category: "Vitamins",
          percentDailyValue: "125%",
          form: "Cholecalciferol",
        },
      ),
      ingredient(
        "Vitamin E",
        "15mg alpha-tocopherol",
        "Fat-soluble antioxidant commonly discussed in sperm oxidative-stress support.",
        makeId("supplement-vitamin-e"),
        {
          category: "Vitamins",
          percentDailyValue: "100%",
          form: "d-alpha tocopherol succinate",
        },
      ),
      ingredient(
        "Vitamin K",
        "100mcg",
        "Fat-soluble nutrient for normal vitamin K status; important to review with anticoagulants.",
        makeId("supplement-vitamin-k"),
        {
          category: "Vitamins",
          percentDailyValue: "83%",
          form: "Phylloquinone",
        },
      ),
      ingredient(
        "Thiamin (B1)",
        "4mg",
        "B-vitamin support for energy metabolism.",
        makeId("supplement-thiamin-b1"),
        {
          category: "B vitamins",
          percentDailyValue: "333%",
          form: "Thiamin hydrochloride",
        },
      ),
      ingredient(
        "Riboflavin (B2)",
        "5mg",
        "Supports flavin coenzymes involved in energy and antioxidant recycling.",
        makeId("supplement-riboflavin-b2"),
        {
          category: "B vitamins",
          percentDailyValue: "385%",
          form: "Riboflavin-5-phosphate",
        },
      ),
      ingredient(
        "Niacin (B3)",
        "30mg NE",
        "Supports NAD/NADP pathways for energy metabolism and cellular repair.",
        makeId("supplement-niacin-b3"),
        {
          category: "B vitamins",
          percentDailyValue: "188%",
          form: "Niacinamide",
        },
      ),
      ingredient(
        "Vitamin B6",
        "10mg",
        "Active-form B6 for amino-acid and methylation-adjacent pathways.",
        makeId("supplement-vitamin-b6"),
        {
          category: "B vitamins",
          percentDailyValue: "588%",
          form: "Pyridoxyl-5-phosphate",
        },
      ),
      ingredient(
        "Folate",
        "1,333mcg DFE",
        "Methylfolate supports DNA synthesis and methylation biology.",
        makeId("supplement-methylfolate"),
        {
          category: "B vitamins",
          percentDailyValue: "333%",
          form: "5-methyltetrahydrofolate",
        },
      ),
      ingredient(
        "Vitamin B12",
        "1,000mcg",
        "Methylcobalamin supports DNA synthesis, methylation, and nervous-system function.",
        makeId("supplement-vitamin-b12"),
        {
          category: "B vitamins",
          percentDailyValue: "41,667%",
          form: "Methylcobalamin",
        },
      ),
      ingredient(
        "Biotin",
        "300mcg",
        "B-vitamin cofactor for metabolism; also relevant to lab-test interference reminders.",
        makeId("supplement-biotin-b7"),
        {
          category: "B vitamins",
          percentDailyValue: "1,000%",
        },
      ),
      ingredient(
        "Pantothenic Acid",
        "10mg",
        "B5 supports coenzyme A and energy metabolism.",
        makeId("supplement-pantothenic-acid-b5"),
        {
          category: "B vitamins",
          percentDailyValue: "200%",
          form: "d-calcium pantothenate",
        },
      ),
      ingredient(
        "Choline",
        "20mg",
        "Methyl donor and membrane nutrient that complements folate/B12 pathways.",
        makeId("supplement-choline"),
        {
          category: "Methylation",
          percentDailyValue: "4%",
          form: "Choline bitartrate",
        },
      ),
      ingredient(
        "Iodine",
        "150mcg",
        "Supports thyroid hormone production, which matters for reproductive health.",
        makeId("supplement-iodine"),
        {
          category: "Minerals",
          percentDailyValue: "100%",
          form: "Potassium iodide",
        },
      ),
      ingredient(
        "Magnesium",
        "10mg",
        "Mineral cofactor for ATP and enzyme systems.",
        makeId("supplement-magnesium"),
        {
          category: "Minerals",
          percentDailyValue: "2%",
          form: "Magnesium citrate",
        },
      ),
      ingredient(
        "Zinc",
        "10mg",
        "Core male fertility mineral involved in spermatogenesis and antioxidant defense.",
        makeId("supplement-zinc"),
        {
          category: "Minerals",
          percentDailyValue: "91%",
          form: "Zinc bisglycinate chelate",
        },
      ),
      ingredient(
        "Selenium",
        "200mcg",
        "Trace mineral for selenoproteins involved in sperm motility and antioxidant defense.",
        makeId("supplement-selenium"),
        {
          category: "Minerals",
          percentDailyValue: "364%",
          form: "L-selenomethionine",
        },
      ),
      ingredient(
        "Copper",
        "2mg",
        "Balances zinc intake and supports antioxidant enzymes.",
        makeId("supplement-copper"),
        {
          category: "Minerals",
          percentDailyValue: "222%",
          form: "Copper bisglycinate chelate",
        },
      ),
      ingredient(
        "Manganese",
        "2mg",
        "Trace-mineral cofactor for mitochondrial antioxidant enzymes.",
        makeId("supplement-manganese"),
        {
          category: "Minerals",
          percentDailyValue: "87%",
          form: "Manganese bisglycinate chelate",
        },
      ),
      ingredient(
        "Chromium",
        "100mcg",
        "Trace mineral tied to glucose/insulin metabolism.",
        makeId("supplement-chromium"),
        {
          category: "Minerals",
          percentDailyValue: "286%",
          form: "Chromium picolinate",
        },
      ),
      ingredient(
        "Molybdenum",
        "75mcg",
        "Trace-mineral cofactor for sulfur-amino-acid metabolism.",
        makeId("supplement-molybdenum"),
        {
          category: "Minerals",
          percentDailyValue: "167%",
          form: "Molybdenum glycinate chelate",
        },
      ),
      ingredient(
        "Ashwagandha extract",
        "360mg",
        "Adaptogenic botanical often marketed for stress and male reproductive support.",
        makeId("supplement-ashwagandha"),
        {
          category: "Botanicals",
          form: "Withania somnifera root",
        },
      ),
      ingredient(
        "L-Carnitine L-Tartrate",
        "310mg",
        "Sperm-energy nutrient commonly studied for motility support.",
        makeId("supplement-l-carnitine"),
        {
          category: "Amino acids",
        },
      ),
      ingredient(
        "Maca powder",
        "310mg",
        "Botanical root marketed for libido and male fertility support; evidence is mixed.",
        makeId("supplement-maca"),
        {
          category: "Botanicals",
          form: "Lepidium meyenii root",
        },
      ),
      ingredient(
        "L-Cysteine hydrochloride",
        "300mg",
        "Glutathione building block for antioxidant defense.",
        makeId("supplement-l-cysteine"),
        {
          category: "Amino acids",
        },
      ),
      ingredient(
        "Alpha Lipoic Acid",
        "200mg",
        "Antioxidant that helps recycle other antioxidants.",
        makeId("supplement-alpha-lipoic-acid"),
        {
          category: "Antioxidants",
        },
      ),
      ingredient(
        "L-Glutathione",
        "100mg",
        "Reduced glutathione is a central antioxidant in cellular redox balance.",
        makeId("supplement-glutathione"),
        {
          category: "Antioxidants",
          form: "Reduced",
        },
      ),
      ingredient(
        "Coenzyme Q10",
        "60mg",
        "Mitochondrial energy and antioxidant nutrient relevant to sperm motility.",
        makeId("supplement-coq10"),
        {
          category: "Antioxidants",
          form: "Ubiquinone",
        },
      ),
      ingredient(
        "Resveratrol 50% extract",
        "48mg",
        "Polyphenol antioxidant from Japanese knotweed.",
        makeId("supplement-resveratrol"),
        {
          category: "Antioxidants",
          form: "Polygonum cuspidatum root",
        },
      ),
      ingredient(
        "L-Methionine",
        "25mg",
        "Essential amino acid feeding methylation pathways.",
        makeId("supplement-l-methionine"),
        {
          category: "Amino acids",
        },
      ),
      ingredient(
        "Lycopene extract 5%",
        "20mg",
        "Tomato carotenoid antioxidant found in reproductive tissues.",
        makeId("supplement-lycopene"),
        {
          category: "Antioxidants",
          form: "Lycopersicum esculentum / tomato",
        },
      ),
      ingredient(
        "Inositol",
        "10mg",
        "Cell-signaling nutrient included at a small dose in this male prenatal formula.",
        makeId("supplement-myo-inositol"),
        {
          category: "Micronutrients",
        },
      ),
      ingredient(
        "L-Arginine hydrochloride",
        "4mg",
        "Nitric-oxide precursor involved in vascular and sperm metabolism pathways.",
        makeId("supplement-l-arginine"),
        {
          category: "Amino acids",
        },
      ),
      ingredient(
        "Astragalus extract",
        "0.30mg",
        "Botanical root extract used as an adaptogenic/immune-support ingredient.",
        makeId("supplement-astragalus"),
        {
          category: "Botanicals",
          form: "Astragalus membranaceus root",
        },
      ),
    ],
    otherIngredients: [
      "Vegetable cellulose capsule",
      "Rice powder",
      "L-leucine",
      "Magnesium stearate",
    ],
    purchase: {
      price: 112.5,
      originalPrice: 125,
      retailPrice: 150,
      currency: "USD",
      discountLabel: "10% off AlphaSperm",
      stripeProductId: "supplement-alphasperm",
      checkoutMode: "stripe",
    },
    shopLinks: [
      {
        label: "Order from AlphaSperm",
        url: ALPHASPERM_PRODUCT_URL,
      },
      {
        label: "View official supplement label",
        url: ALPHASPERM_LABEL_URL,
      },
    ],
    interactions: [
      {
        with: "Warfarin or anticoagulants",
        severity: "critical",
        concern:
          "AlphaSperm contains vitamin K plus botanicals and antioxidants that may complicate anticoagulation management.",
        recommendation:
          "Do not start without explicit approval from the clinician managing anticoagulation.",
        linkedBlockId: makeId("med-warfarin"),
      },
      {
        with: "Levothyroxine or thyroid medication",
        severity: "caution",
        concern:
          "Minerals such as magnesium and zinc can reduce thyroid medication absorption, and the formula contains iodine and ashwagandha.",
        recommendation:
          "Separate thyroid medication from supplements as directed and tell your clinician about the full formula.",
        linkedBlockId: makeId("med-levothyroxine"),
      },
      {
        with: "Other multivitamins or male fertility stacks",
        severity: "caution",
        concern:
          "Duplicate zinc, selenium, iodine, vitamin A, B6, vitamin K, CoQ10, ashwagandha, NAC/cysteine, or glutathione can push totals above intended ranges.",
        recommendation:
          "Compare labels before combining products and bring the full list to your care team.",
      },
    ],
  },
  sources: [
    {
      title: "AlphaSperm product page",
      journal: "AlphaSperm Inc",
      year: 2026,
      url: ALPHASPERM_PRODUCT_URL,
      evidenceLevel: "expert-opinion",
    },
    {
      title: "AlphaSperm official supplement label",
      journal: "AlphaSperm Inc",
      year: 2026,
      url: ALPHASPERM_LABEL_URL,
      evidenceLevel: "expert-opinion",
    },
    {
      title: "AlphaSperm Men's Prenatal+ Quality Certification Program",
      journal: "ConsumerLab",
      year: 2025,
      url: "https://cdn.shopify.com/s/files/1/0039/7321/8393/files/ConsumerLabs_-_AlphaSperm_Report_2026.pdf",
      evidenceLevel: "review",
    },
    {
      title:
        "AlphaSperm-induced epigenetic reprogramming in sperm of infertile men",
      journal: "Fertility and Sterility",
      year: 2024,
      url: "https://www.fertstert.org/article/S0015-0282(24)01901-0/fulltext",
      evidenceLevel: "observational",
    },
  ],
};

registerBlock(vitaminA);
registerBlock(vitaminK);
registerBlock(thiamin);
registerBlock(riboflavin);
registerBlock(niacin);
registerBlock(vitaminB6);
registerBlock(vitaminB12);
registerBlock(biotin);
registerBlock(pantothenicAcid);
registerBlock(copper);
registerBlock(manganese);
registerBlock(chromium);
registerBlock(molybdenum);
registerBlock(lCysteine);
registerBlock(glutathione);
registerBlock(lMethionine);
registerBlock(lycopene);
registerBlock(astragalus);
registerBlock(alphaSperm);
